import { Component, OnInit, OnDestroy, signal, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActualitesService } from '../actualites.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

import { isAllowedImage, readImagePreview, IMAGE_FORMAT_ERROR } from '../../../../shared/utils/image-upload';
export interface Actualite {
  news_id?: number;
  news_name: string;
  news_excerpt?: string;
  news_content: string;
  news_author?: string;
  news_begin_date: string;
  news_tags?: string;
  news_status: 'ACTI' | 'INAC' | 'ARCH';
  file_link?: string;
}

@Component({
  selector: 'app-actualites-form',
  standalone: false,
  templateUrl: './actualites-form.component.html',
  styleUrls: ['./actualites-form.component.scss']
})
export class ActualitesFormComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('visualEditor') visualEditor!: ElementRef<HTMLDivElement>;

  actualiteId = signal<number | null>(null);
  isLoading = signal(false);
  isFetching = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  imagePreview = signal<string | null>(null);
  currentImageFile = signal<File | null>(null);

  /** 'visual' = WYSIWYG Aperçu, 'code' = raw HTML Code */
  editorMode = signal<'visual' | 'code'>('visual');

  formData = signal<Actualite>({
    news_name: '',
    news_excerpt: '',
    news_content: '',
    news_author: '',
    news_begin_date: '',
    news_tags: '',
    news_status: 'ACTI'
  });

  private destroy$ = new Subject<void>();
  private needsVisualSync = false;

  constructor(
    private actualitesService: ActualitesService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.actualiteId.set(params['id']);
        this.loadActualite(params['id']);
      } else {
        this.actualiteId.set(null);
        this.resetForm();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sync formData.news_content → contenteditable div whenever we switch to visual mode
   * or after data is loaded.
   */
  ngAfterViewChecked(): void {
    if (this.needsVisualSync && this.visualEditor?.nativeElement) {
      const el = this.visualEditor.nativeElement;
      if (el.innerHTML !== this.formData().news_content) {
        el.innerHTML = this.formData().news_content || '';
      }
      this.needsVisualSync = false;
    }
  }

  private loadActualite(id: number): void {
    this.isFetching.set(true);
    this.errorMessage.set(null);

    this.actualitesService.getActualiteById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const a = response.data || response;
          this.formData.set({
            news_id: a.news_id,
            news_name: a.news_name || '',
            news_excerpt: a.news_excerpt || '',
            news_content: a.news_content || '',
            news_author: a.news_author || '',
            news_begin_date: this.formatDateForInput(a.news_begin_date),
            news_tags: a.news_tags || '',
            news_status: a.news_status || 'ACTI'
          });
          if (a.file_link) this.imagePreview.set(a.file_link);
          this.needsVisualSync = true;
          this.isFetching.set(false);
        },
        error: (err) => {
          this.isFetching.set(false);
          SwalUtils.error('Erreur', err?.error?.message || 'Impossible de charger cette actualité.');
        }
      });
  }

  private resetForm(): void {
    this.formData.set({
      news_name: '', news_excerpt: '', news_content: '',
      news_author: '', news_begin_date: '', news_tags: '', news_status: 'ACTI'
    });
    this.imagePreview.set(null);
    this.currentImageFile.set(null);
    this.errorMessage.set(null);
    this.successMessage.set(null);
    this.needsVisualSync = true;
  }

  // ── Editor mode switching ─────────────────────────────────────────────────

  setEditorMode(mode: 'visual' | 'code'): void {
    if (mode === this.editorMode()) return;

    if (this.editorMode() === 'visual' && this.visualEditor?.nativeElement) {
      // Flush visual editor HTML to formData before switching to code view
      this.formData.set({ ...this.formData(), news_content: this.visualEditor.nativeElement.innerHTML });
    }

    this.editorMode.set(mode);

    if (mode === 'visual') {
      this.needsVisualSync = true;
    }
  }

  // ── Visual (WYSIWYG) editor ───────────────────────────────────────────────

  onVisualInput(): void {
    if (this.visualEditor?.nativeElement) {
      this.formData.set({
        ...this.formData(),
        news_content: this.visualEditor.nativeElement.innerHTML
      });
    }
  }

  /** Execute formatting command on current selection inside contenteditable */
  execFormat(command: string, value: string = ''): void {
    this.visualEditor?.nativeElement?.focus();
    document.execCommand(command, false, value || undefined);
    // Sync innerHTML back to formData
    this.onVisualInput();
  }

  insertHeading(level: 'h2' | 'h3'): void {
    this.execFormat('formatBlock', level);
  }

  insertParagraph(): void {
    this.execFormat('formatBlock', 'p');
  }

  insertLink(): void {
    const url = prompt('URL du lien :');
    if (url) this.execFormat('createLink', url);
  }

  // ── Code (raw HTML) editor ────────────────────────────────────────────────

  onCodeInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.formData.set({ ...this.formData(), news_content: value });
  }

  // ── Image ─────────────────────────────────────────────────────────────────

  private readonly IMAGE_MAX_WIDTH  = 1280;
  private readonly IMAGE_MAX_HEIGHT = 720;
  private readonly IMAGE_MAX_SIZE   = 5 * 1024 * 1024; // 5 MB

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!isAllowedImage(file)) { this.errorMessage.set(IMAGE_FORMAT_ERROR); return; }
    if (file.size > this.IMAGE_MAX_SIZE) { this.errorMessage.set('Fichier trop volumineux. Maximum 5 Mo.'); return; }

    readImagePreview(file, (dataUrl) => {
      this.currentImageFile.set(file);
      this.imagePreview.set(dataUrl);
    });
  }

  imageRemoved = signal(false);

  removeImage(): void {
    this.imagePreview.set(null);
    this.currentImageFile.set(null);
    this.imageRemoved.set(true);
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async onSave(): Promise<void> {
    // Flush visual editor before saving
    if (this.editorMode() === 'visual' && this.visualEditor?.nativeElement) {
      this.formData.set({ ...this.formData(), news_content: this.visualEditor.nativeElement.innerHTML });
    }

    if (!this.formData().news_name.trim()) { this.errorMessage.set('Le titre est requis'); return; }
    if (!this.formData().news_content.replace(/<[^>]*>/g, '').trim()) { this.errorMessage.set('Le contenu est requis'); return; }
    if (!this.formData().news_begin_date) { this.errorMessage.set('La date de publication est requise'); return; }

    // Capture before await — signal could change during dialog
    const editId = this.actualiteId();
    const isUpdate = editId !== null;

    const confirmed = await SwalUtils.confirmGreen(
      isUpdate ? 'Modifier l\'actualité' : 'Créer l\'actualité',
      isUpdate
        ? 'Voulez-vous enregistrer les modifications de cette actualité ?'
        : 'Voulez-vous créer cette actualité ?',
      isUpdate ? 'Modifier' : 'Créer'
    );
    if (!confirmed.isConfirmed) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    const form = new FormData();
    form.append('news_name', this.formData().news_name);
    form.append('news_excerpt', this.formData().news_excerpt || '');
    form.append('news_content', this.formData().news_content);
    form.append('news_author', this.formData().news_author || '');
    form.append('news_begin_date', this.formData().news_begin_date);
    if (this.formData().news_tags) form.append('news_tags', this.formData().news_tags!);
    form.append('news_status', this.formData().news_status);
    if (this.currentImageFile()) form.append('image', this.currentImageFile()!);
    else if (isUpdate && this.imageRemoved()) form.append('remove_image', '1');

    const req$ = isUpdate
      ? this.actualitesService.updateActualite(editId!, form)
      : this.actualitesService.createActualite(form);

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        SwalUtils.success(
          isUpdate ? 'Actualité modifiée' : 'Actualité créée',
          isUpdate ? 'Les modifications ont été enregistrées avec succès.' : 'L\'actualité a été créée avec succès.'
        );
        const id = isUpdate ? this.actualiteId()! : res?.data?.news_id;
        if (id) {
          this.router.navigate(['/federation/actualites', id]);
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        SwalUtils.error('Erreur', err?.error?.message || 'Une erreur est survenue lors de l\'enregistrement.');
      }
    });
  }

  onCancel(): void { this.router.navigate(['/federation/actualites']); }

  updateField(field: keyof Actualite, value: any): void {
    this.formData.set({ ...this.formData(), [field]: value });
  }

  private formatDateForInput(dateStr: string): string {
    if (!dateStr) return '';
    try { return new Date(dateStr).toISOString().split('T')[0]; }
    catch { return dateStr; }
  }

  get isEditMode(): boolean { return this.actualiteId() !== null; }
  get pageTitle(): string { return this.isEditMode ? 'Modifier l\'actualité' : 'Créer une actualité'; }
}
